import { useEffect, useState } from "react";
import API_BASE from "../config";

// Hard-coded fallback used until /api/site-content responds (or if the
// request fails). Mirrored on the backend in SettingController.DEFAULTS
// so there is no drift between the two layers.
export const COMPANY_DEFAULTS_FALLBACK = {
  "work.defaultOvertimeMinutes":         "30",
  "work.defaultPolicy":                  "STRICT",
  "work.defaultSchedStart":              "08:00",
  "work.defaultSchedEnd":                "17:00",
  // "monthly" — salary ÷ actual weekdays this month (default).
  // "daily"   — salary used directly per day.
  "work.defaultSalaryType":              "monthly",
  // "true" → apply late/undertime deductions on Sundays + Holiday rows.
  // "false" → skip those deductions; only total hours + OT count.
  "work.defaultApplyDeductionsOnDayOff": "false",
  // JSON array of { name, days, convertible } leave-benefit rows
  // seeded onto new employees. Empty string = no default; new hires
  // start blank and the admin adds leaves manually.
  "work.defaultLeaves":                  "",
  // Percentage of daily rate paid when converting one unused
  // convertible leave day to cash at year-end. "100" = full day.
  "work.defaultLeaveConversionPercent":  "100",
  // When "true" the Sales / Inventory features are disabled: the
  // Dashboard / Inventory / Sales nav tabs are hidden from the
  // Topbar, the Reports page's Inventory & Sales tab is disabled,
  // and the per-role Sales / Inventory permission editor is hidden
  // from the System Roles tab. The underlying features stay installed.
  // Toggle lives on the Maintenance tab in Settings (SuperAdmin only);
  // the label there reads "Enable Sales / Inventory features" and
  // stores the inverted value.
  "ui.hideSalesRoles":                   "false",
  // When "true" the Payroll built-in roles (Manager, Employee, Staff)
  // are hidden from the Roles tab and user-creation dropdown, matching
  // user accounts are hidden from the Users tab, and the Profile /
  // Timesheet / Calendar nav tabs are hidden from the Topbar.
  "ui.hidePayrollRoles":                 "false",
  // Job Roles catalog — three free-form lists grouped by category,
  // shown on Settings → Job Roles. Stored as a JSON string keyed by
  // category:  { management: [...], operations: [...], support: [...] }
  "work.jobRoles":                       "",
};

// Tiny in-memory cache + subscriber list so every component that uses
// the hook shares one fetch and every Settings-page save is reflected
// everywhere else without a reload.
let cache = { ...COMPANY_DEFAULTS_FALLBACK };
let hydrated = false;
let inflight = null;
const listeners = new Set();

function notify() {
  for (const fn of listeners) fn({ ...cache });
}

export async function fetchCompanyDefaults(force = false) {
  if (!force && hydrated) return cache;
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/site-content`)
    .then(r => r.ok ? r.json() : {})
    .then(data => {
      cache = { ...COMPANY_DEFAULTS_FALLBACK, ...(data || {}) };
      hydrated = true;
      notify();
      return cache;
    })
    .catch(() => cache)
    .finally(() => { inflight = null; });
  return inflight;
}

export async function saveCompanyDefaults(partial, role) {
  const res = await fetch(`${API_BASE}/api/site-content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "X-User-Role": role || "" },
    body: JSON.stringify(partial),
  });
  if (!res.ok) throw new Error(`PUT /api/site-content failed: ${res.status}`);
  const merged = await res.json();
  cache = { ...COMPANY_DEFAULTS_FALLBACK, ...(merged || {}) };
  hydrated = true;
  notify();
  return cache;
}

/**
 * React hook that returns the current company defaults. Triggers one
 * fetch on first mount and auto-updates when another component saves
 * via saveCompanyDefaults(). Always returns a usable object (falls
 * back to COMPANY_DEFAULTS_FALLBACK before the fetch completes).
 */
export function useCompanyDefaults() {
  const [val, setVal] = useState(cache);
  useEffect(() => {
    listeners.add(setVal);
    fetchCompanyDefaults();
    return () => { listeners.delete(setVal); };
  }, []);
  return val;
}

/**
 * Resolve an employee's effective work settings by falling back to the
 * company defaults for any null field. Pure — call from anywhere that
 * already has the defaults in scope (typically via useCompanyDefaults).
 */
export function resolveEmployeeWorkSettings(emp, defaults) {
  const d = defaults || COMPANY_DEFAULTS_FALLBACK;
  const defaultApply = String(d["work.defaultApplyDeductionsOnDayOff"] ?? "false").toLowerCase() === "true";
  return {
    schedStart: emp?.expectedStart || d["work.defaultSchedStart"] || "08:00",
    schedEnd:   emp?.expectedEnd   || d["work.defaultSchedEnd"]   || "17:00",
    overtimeStartMinutes:
      emp?.overtimeStartMinutes != null
        ? Number(emp.overtimeStartMinutes)
        : Number(d["work.defaultOvertimeMinutes"] ?? 30),
    workTimePolicy: emp?.workTimePolicy || d["work.defaultPolicy"] || "STRICT",
    // Null on the employee → inherit company default. Non-null → use
    // the explicit true/false override.
    applyDeductionsOnDayOff:
      emp?.applyDeductionsOnDayOff != null
        ? !!emp.applyDeductionsOnDayOff
        : defaultApply,
    salaryType: emp?.salaryType || d["work.defaultSalaryType"] || "monthly",
  };
}
